import React from "react";
import { t } from "ttag";

import type {
    ServerCmsTypesCategoryProductFacet as CategoryProductFacet,
    Money,
    ServerCmsTypesProductSummary as ProductSummary,
} from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";
import { getStyleVariantClass } from "@thelabnyc/thelabui/src/utils/styles";

import { Themed } from "../../styles/themes";
import { delegateClickToLink } from "../../utils/delegateClickToLink";
import { fallbackTileImage } from "../../utils/fallbackTileImage";
import { Clickable } from "../Clickables";
import { FavoriteToggleButton } from "../FavoriteToggleButton";
import { ProductStatusBadge } from "../ProductStatusBadge";

import categoryStyles from "../CategoryContentBlock/index.module.scss";
import styles from "./index.module.scss";

export interface ProductCardProps {
    product: ProductSummary;
    tag?: keyof React.JSX.IntrinsicElements;
    layout: "vertical" | "horizontal" | "horizontalLarge";
    skeleton?: boolean;
    showFavoriteButton?: boolean;
}

const formatPrice = (msrp: Money) => msrp.formatted;

/**
 * Since we're using subgrids, we're depending on a certain DOM structure.
 * So, in the unlikely event that a piece is missing, ProductCard's grid will
 * handle it elegantly.
 */
const Empty = () => <div />;

const FacetList = ({
    facetValues,
}: {
    facetValues: CategoryProductFacet[];
}) => {
    if (facetValues.length === 0)
        return (
            <>
                <Empty />
                <Empty />
            </>
        );

    /**
     * Design planned for 4 facets; it's a 2×2 subgrid to achieve extremely
     * satisfying levels of grid alignment.
     *
     * Anything from 0 to 4 should work fine. But if it went to 5, it'd break
     * the grid's assumptions. So we'd have to consider whether to make it a
     * 2x3 subgrid or removing the subgrid entirely.
     */
    const MAX_FACETS = 4;

    const values = facetValues.slice(0, MAX_FACETS);

    return (
        <dl className={styles.facetList}>
            {values.map((facetValue) => (
                <div key={`${facetValue.facet_slug}-${facetValue.value_slug}`}>
                    <dt className={styles.facetLabel}>{facetValue.name}</dt>
                    <dd className={styles.facetValue}>
                        {facetValue.display_value}
                    </dd>
                </div>
            ))}
        </dl>
    );
};

export const ProductCard = ({
    product,
    tag,
    layout,
    skeleton,
    showFavoriteButton = true,
}: ProductCardProps) => {
    const { title, url, sku, image, msrp, featured_facets, status } = product;
    const isDiscontinued = status === "discontinued";
    const cardClassName = getStyleVariantClass(
        {
            vertical: styles.cardVertical,
            horizontal: styles.cardHorizontal,
            horizontalLarge: styles.cardHorizontalLarge,
        },
        layout,
    );

    return (
        <Themed
            tag={tag ?? "div"}
            theme={layout === "horizontalLarge" ? "white" : "offWhite"}
            className={cardClassName}
            onClick={delegateClickToLink}
            inert={skeleton || undefined}
        >
            <div className={styles.header}>
                <p className={styles.sku}>{sku}</p>

                <h2 className={styles.title}>
                    {url ? <Clickable href={url}>{title}</Clickable> : title}
                </h2>

                <ProductStatusBadge
                    status={status}
                    className={
                        layout === "horizontal"
                            ? styles.badgeFloated
                            : styles.badgeInline
                    }
                />

                {layout === "horizontalLarge" && url && (
                    <div className={styles.detailLink} aria-hidden="true">
                        {t`View Product Detail Page`}
                    </div>
                )}

                {showFavoriteButton && !skeleton && (
                    <FavoriteToggleButton
                        product={{ name: title, sku, url: url ?? "" }}
                        removeOnly={!url}
                        image={image}
                        className={styles.favoriteButton}
                        svgClassName={styles.favoriteSvg}
                    />
                )}
            </div>

            <ImageChooserBlock
                value={image ?? fallbackTileImage}
                attrs={{ className: styles.picture }}
                imageAttrs={{ className: styles.image, alt: "" }}
                sizes={categoryStyles.gridCardSizes}
            />

            <FacetList facetValues={featured_facets} />

            {msrp && !isDiscontinued && layout !== "horizontalLarge" ? (
                <p className={styles.price}>{formatPrice(msrp)}</p>
            ) : (
                <Empty />
            )}
        </Themed>
    );
};
